import React, { ReactNode, useRef } from 'react';
import styled from '@emotion/styled';
import { useButton } from 'react-aria';
import { ThemeProps } from '../../../types';

type Variants =
  | 'accent'
  | 'link'
  | 'primary'
  | 'secondary'
  | 'transparent'
  | 'withInput'
  | 'small';
export interface ButtonProps {
  /**
   * Button contents
   */
  children: ReactNode;
  /**
   * The css class for the input. Handled internally by emotion
   */
  className: string;
  /**
   * Boolean to disable this button
   */
  disabled?: boolean;
  /**
   * Displays the loading animation
   */
  isBusy?: boolean;
  /**
   * Displays the loading text
   */
  isBusyText?: string;
  /**
   * Click handler
   */
  onClick: () => void;
  /**
   * Type of button to display
   */
  type: 'button' | 'submit';
  /**
   * The type of button to display
   */
  variant: Variants;
}

const applyLinkStyles = ({ colors, fonts }: ThemeProps, variant: Variants) => {
  const variantStyles = {
    accent: {},
    link: {
      backgroundColor: 'transparent',
      border: 0,
      color: colors.text.primary,
      fontSize: 16,
      fontWeight: fonts.weights.medium,
      height: 'auto',
      minWidth: 'auto',
      padding: 0,
      textDecoration: 'underline',
      width: 'auto',
    },
    primary: {},
    secondary: {},
    transparent: {},
    withInput: {},
    small: {
      display: 'inline-block',
      color: colors.text.white,
      alignSelf: 'flex-end',
      border: 0,
      fontSize: '16px',
      fontWeight: fonts.weights.medium,
      width: '60px',
      minWidth: '60px',
      height: '30px',
      minHeight: '30px',
      padding: '0',
      marginLeft: '10px',
    }
  };

  return variantStyles[variant];
};

const Button = (props: ButtonProps) => {
  const {
    children,
    className,
    disabled,
    isBusy,
    isBusyText,
    onClick,
    type = 'button',
  }: ButtonProps = props;
  const ref: any = useRef();
  const { buttonProps } = useButton({ ...props }, ref);

  return (
    <button
      {...buttonProps}
      className={className}
      disabled={!!disabled || isBusy}
      onClick={onClick}
      ref={ref}
      type={type}>
      {isBusy ? isBusyText : children}
    </button>
  );
};

const StyledButton = styled((props: any) => <Button {...props} />)`
  ${({ theme, variant }: { theme: ThemeProps; variant: Variants }) => ({
    alignItems: 'center',
    backgroundColor:
      variant === 'transparent'
        ? 'transparent'
        : variant === 'accent'
        ? theme.colors.common.white
        : theme.colors.accent,
    borderColor:
      variant === 'transparent' ? 'transparent' : theme.colors.accent,
    borderStyle: 'solid',
    borderRadius: theme.radii.button,
    borderWidth: 1,
    color:
      variant === 'accent'
        ? theme.colors.text.primary
        : theme.colors.text.white,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    fontSize: variant === 'withInput' ? 16 : 20,
    fontWeight: 600,
    height: 48,
    justifyContent: 'center',
    minWidth: variant === 'withInput' ? 'auto' : 200,
    paddingLeft: 20,
    paddingRight: 20,
    '&:hover': {
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.8,
      transform: 'scale(0.99)',
    },
    '&:disabled': {
      backgroundColor: theme.colors.common.darkGrey,
      borderColor: theme.colors.common.darkGrey,
      cursor: 'not-allowed',
    },
    ...applyLinkStyles(theme, variant),
  })}
`;

export default StyledButton;
